import { useEffect, useRef } from "react"
import type { FC, MouseEvent, ReactElement, ReactNode, RefObject } from "react"

import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from "body-scroll-lock"
import isString from "lodash-es/isString"
import { useKeyPressEvent, useMount, useUnmount } from "react-use"

import { CrossIcon } from "@/3514/assets"
import {
    Button,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { EModalId, type IModal, type TModalDispatcher, useModalDispatcher, useModalSelector } from "@/3514/store/modal"
import { buildCssClassName, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import ModalPortal from "./modal.portal.component"
import classes from "./modal.styles"

type _TProps = {
    modalId: EModalId
    modalBodyClassName?: string
    modalHeaderClassName?: string
    modalWrapperClassName?: string
    title?: string | ReactNode
    onHide?: TEmptyCallback
    withDisabledBodyScroll?: boolean
    withHideOnOutsideClick?: boolean
    withHideOnEscapeClick?: boolean
    withStickyHeading?: boolean
    withCloseIcon?: boolean
    withPortal?: boolean
    children: ReactNode
}
const Modal: FC<_TProps> = ({
    modalId,
    onHide,
    title = String(),
    modalBodyClassName = String(),
    modalHeaderClassName = String(),
    modalWrapperClassName = String(),
    withDisabledBodyScroll = false,
    withHideOnOutsideClick = false,
    withHideOnEscapeClick = false,
    withPortal = false,
    withCloseIcon = false,
    withStickyHeading = false,
    children
}: _TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const dispatcher: TModalDispatcher = useModalDispatcher()

    const modal: IModal = useModalSelector(modalId)

    function handleHide(): void {
        dispatcher.hideModal(modalId)
        onHide?.()
    }

    const modalBodyRef: RefObject<HTMLDialogElement> = useRef<HTMLDialogElement>(null)

    const bodyRef: RefObject<HTMLElement> = useRef<HTMLElement>(document.body)

    useMount((): void => {
        if (!bodyRef.current || !withDisabledBodyScroll) return

        disableBodyScroll(bodyRef.current, { allowTouchMove: (): boolean => true })
    })

    useUnmount((): void => {
        if (!bodyRef.current || !withDisabledBodyScroll) return

        enableBodyScroll(bodyRef.current)

        clearAllBodyScrollLocks()
    })

    useKeyPressEvent("Escape", (): void => modal?.isOpen && withHideOnEscapeClick && handleHide())

    useEffect((): void => (modal?.isOpen && modalBodyRef.current?.focus(), void 0), [modal?.isOpen])

    function handleOverlayClick(): void {
        if (withHideOnOutsideClick) handleHide()
    }

    function handleWrapperClick(event: MouseEvent): void {
        event.stopPropagation()
    }

    return modal.isOpen ? (
        <ModalPortal withPortal={withPortal}>
            <dialog
                ref={modalBodyRef}
                open={modal.isOpen}
                onClick={handleOverlayClick}
                onKeyDown={emptyCallback}
                className={buildCssClassName(classes.modalWrapper(), {
                    [modalWrapperClassName]: modalWrapperClassName
                })}
            >
                <div
                    onClick={handleWrapperClick}
                    className={buildCssClassName(classes.modalBody(modal.isOpen), {
                        [modalBodyClassName]: modalBodyClassName
                    })}
                >
                    {withCloseIcon && (
                        <Button
                            isContainer
                            aria-label={t("common.close")}
                            className={buildCssClassName(classes.closeBtn())}
                            onClick={withPressHandler(handleHide)}
                            onKeyDown={withPressHandler(handleHide)}
                        >
                            <CrossIcon />
                        </Button>
                    )}

                    <div
                        className={buildCssClassName(classes.modalHeader(withStickyHeading), {
                            [modalHeaderClassName]: modalHeaderClassName
                        })}
                    >
                        {isString(title) ? (
                            <Typography
                                text={title}
                                tag={ETypographyTag.Heading2}
                                color={ETypographyColor.Black}
                                size={ETypographySize.Heading2}
                                alignment={ETypographyAlignment.Center}
                                weight={ETypographyWeight.Medium}
                                family={ETypographyFamily.DmSerif}
                                className="mb-0"
                            />
                        ) : (
                            title
                        )}
                    </div>
                    <div className={classes.modalContent()}>{children}</div>
                </div>
            </dialog>
        </ModalPortal>
    ) : null
}

Modal.displayName = "Modal"

export { Modal }
